/**
 * The Filing screen's sentences (D-05) — pure, so `tests/filing-lane.test.ts` can hold them.
 *
 * The screen renders and never decides (ADR-0014): whether the lane is on, the session's state
 * and its times all come from `GET /filing/{client_id}`. This turns them into sentences a
 * practitioner reads — never a slug, a mode code or an ISO timestamp.
 */

import { ApiError, type FilingState } from './api'

const IST_OFFSET_MINUTES = 330
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `2026-10-09T11:45:00+00:00` → `17:15 on 9 Oct 2026` (IST, the portal's clock). */
export function istWords(iso: string | null): string | null {
  if (!iso) return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  const ist = new Date(at.getTime() + IST_OFFSET_MINUTES * 60_000)
  const hh = String(ist.getUTCHours()).padStart(2, '0')
  const mm = String(ist.getUTCMinutes()).padStart(2, '0')
  return `${hh}:${mm} on ${ist.getUTCDate()} ${MONTHS[ist.getUTCMonth()]} ${ist.getUTCFullYear()}`
}

/** `sandbox` → words. A mode the screen has never heard of is said as "an unnamed …". */
export function environmentWords(environment: string | null): string | null {
  if (environment === 'sandbox') return "GSTN's sandbox (test credentials, no real return)"
  if (environment === 'production') return 'the live GST portal'
  return environment ? 'an environment this screen does not recognise' : null
}

/** The lane in one sentence, or null when it is on. */
export function laneSentence(state: FilingState): string | null {
  return state.lane.mode === 'off' ? (state.lane.sentence ?? 'Filing is switched off.') : null
}

/** The session's state and, when it has them, its times. */
export function sessionWords(state: FilingState): string {
  const s = state.session
  const parts = [s.words]
  const expires = istWords(s.expires_at)
  const reauth = istWords(s.reauth_at)
  if ((s.state === 'live' || s.state === 'reauth_due') && expires) {
    parts.push(`It ends at ${expires}${reauth ? `; a new OTP is asked for from ${reauth}` : ''}.`)
  }
  const where = environmentWords(s.environment)
  if (where && s.state !== 'none' && s.state !== 'expired') parts.push(`Opened against ${where}.`)
  return parts.join(' ')
}

/** The username line: masked, or what to do. */
export function usernameWords(state: FilingState): string {
  if (state.username_masked) return `GST portal username on file: ${state.username_masked}`
  if (!state.username_storable) {
    return 'GST portal usernames cannot be stored on this deployment yet: the key that seals them is not configured.'
  }
  return 'No GST portal username on file for this client yet.'
}

/** Whether this person may request an OTP and open a session here (ND-E12). */
export function canAuthenticate(
  state: FilingState,
  can: (resource: string, verb: 'can_update') => boolean,
): boolean {
  return state.lane.mode !== 'off' && can('period', 'can_update') && can('filing', 'can_update')
}

/** A refusal in words. Step-up has its own sentence; everything else is the service's own. */
export function refusalWords(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'STEPUP_REQUIRED' || error.code === 'STEPUP_INVALID') {
      return 'Your two-step verification was not accepted or has expired; confirm the step again with a fresh code.'
    }
    return error.message
  }
  return 'That could not be done.'
}
